import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Link,
  Alert,
  LinearProgress
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth.service';
import { AuthSplitLayout, authFooterSx } from './AuthSplitLayout';
import { AuthTextField } from './AuthTextField';
import { PageHeader } from '../ui/PageHeader';
import { SurfaceCard } from '../ui/SurfaceCard';

const STRENGTH_KEYS = ['veryWeak', 'weak', 'fair', 'good', 'strong'] as const;

interface ChangePasswordFormProps {
  isMandatory?: boolean;
  onSuccess?: () => void;
}

export const ChangePasswordForm = ({ isMandatory = false, onSuccess }: ChangePasswordFormProps) => {
  const { t } = useTranslation('auth');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const getPasswordStrength = (password: string): { strength: number; text: string; color: string } => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    const colors = ['var(--danger)', 'var(--warn)', 'var(--warn)', 'var(--success)', 'var(--success)'];
    
    return {
      strength: (score / 5) * 100,
      text: t(`changePassword.strength.${STRENGTH_KEYS[score] ?? 'veryWeak'}`),
      color: colors[score] || 'var(--danger)'
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('changePassword.mismatch'));
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError(t('changePassword.tooShort', { min: 8 }));
      setLoading(false);
      return;
    }

    try {
      await authService.changePassword({
        currentPassword,
        newPassword
      });
      
      setSuccess(t('changePassword.success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      if (onSuccess) {
        onSuccess();
      } else if (isMandatory) {
        // For mandatory password changes, redirect to home page after success
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || t('changePassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const formElement = (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <AuthTextField
        type="password"
        label={t('changePassword.current')}
        autoComplete="current-password"
        value={currentPassword}
        onChange={e => setCurrentPassword(e.target.value)}
        required
        disabled={loading}
      />
      
      <AuthTextField
        type="password"
        label={t('changePassword.new')}
        autoComplete="new-password"
        value={newPassword}
        onChange={e => setNewPassword(e.target.value)}
        required
        disabled={loading}
        helperText={newPassword && t('changePassword.strengthLabel', { strength: passwordStrength.text })}
      />
      
      {newPassword && (
        <Box sx={{ mt: -1 }}>
          <LinearProgress
            variant="determinate"
            value={passwordStrength.strength}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'var(--bar)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: passwordStrength.color,
                borderRadius: 4,
              },
            }}
          />
        </Box>
      )}
      
      <AuthTextField
        type="password"
        label={t('changePassword.confirm')}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        required
        disabled={loading}
        error={!!(confirmPassword && newPassword !== confirmPassword)}
        helperText={
          confirmPassword && newPassword !== confirmPassword 
            ? t('changePassword.mismatchShort')
            : ''
        }
      />
      
      {success && (
        <Alert severity="success">
          {success}
          {isMandatory && <Typography variant="body2" sx={{ mt: 1 }}>{t('changePassword.redirecting')}</Typography>}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}
      
      <Button
        fullWidth
        variant="contained"
        type="submit"
        disabled={
          loading || 
          !currentPassword.trim() || 
          !newPassword.trim() || 
          !confirmPassword.trim() ||
          newPassword !== confirmPassword
        }
        sx={{ height: 42, mt: 0.5 }}
      >
        {loading ? t('changePassword.submitting') : t('changePassword.submit')}
      </Button>
      
      {!isMandatory && (
        <Box sx={authFooterSx}>
          <Link component={RouterLink} to="/profile" underline="hover">{t('changePassword.backToProfile')}</Link>
        </Box>
      )}
    </Box>
  );

  if (isMandatory) {
    return (
      <AuthSplitLayout
        title={t('changePassword.mandatory.title')}
        subtitle={t('changePassword.mandatory.subtitle')}
        headline={t('changePassword.mandatory.headline')}
        tagline={t('changePassword.mandatory.tagline')}
      >
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>{t('changePassword.mandatory.alertTitle')}</strong><br />
            {t('changePassword.mandatory.alertBody')}
          </Typography>
        </Alert>
        {formElement}
      </AuthSplitLayout>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader title={t('changePassword.title')} subtitle={t('changePassword.subtitle')} />
      <SurfaceCard sx={{ maxWidth: 520 }} bodyPadding="22px">
        {formElement}
      </SurfaceCard>
    </Box>
  );
};
